import { useForm } from "@refinedev/react-hook-form";
import { Typography, Stack } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useCustom, useGetIdentity, useList, useTranslate } from "@refinedev/core";
import { BroadcastMemberColumn } from "@/components/broadcast";
import { CheckboxElement, TextFieldElement } from "react-hook-form-mui";
import { zodResolver } from "@hookform/resolvers/zod";
import { IBroadcastCreateFormSchema, IBroadcastEditFormSchema, broadcastCreateFormSchema, broadcastEditFormSchema, IBroadcastMemberSchema, IBroadcastFilterType } from "@/utils/zod/broadcastFormValidation";
import { CreateOrEdit } from "@/components/editcreate/CreateOrEdit";
import RHFAutoCompleteString from "@/components/form/RHFAutocompleteString";
import { CheckboxState } from '@/components/override/checkbox';
import { useMemo, useState } from 'react';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { IContact } from "@/utils/zod/contactFormValidation";
import { CustomException } from "@/utils/zod/broadcastRoomSchema";
import { IRoom } from "@/utils/zod/roomSchema";

export const BroadcastCreateEdit = ({ actionType }: { actionType: "create" | "edit" }) => {

    const formValues = useForm<typeof actionType extends "create" ? IBroadcastCreateFormSchema : IBroadcastEditFormSchema>({
        refineCoreProps: {
            resource: "rooms/broadcasts",
            redirect: "list",
            queryOptions: {
                select: (data: any) => {
                    const transformedData = {
                        data: {
                            id: data.data.id,
                            room_id: data.data.broadcast_setting.room_id,
                            broadcast_name: data.data.name,
                            sender_phone: `${(data.data.broadcast_setting.sender_account.name && data.data.broadcast_setting.sender_account.user?.name) ? `${data.data.broadcast_setting.sender_account.name} (${data.data.broadcast_setting.sender_account.user?.name})` : data.data.broadcast_setting.sender_account.name ? data.data.broadcast_setting.sender_account.name : data.data.broadcast_setting.sender_account.user?.name ? `(${data.data.broadcast_setting.sender_account.user?.name})` : ""}: ${data.data.broadcast_setting.sender_account.user?.phone_number || ""}`,
                            description: data.data.description,
                            allow_personal: data.data.broadcast_setting.send_to.includes("personal"),
                            allow_group: data.data.broadcast_setting.send_to.includes("group"),
                            must_send: data.data.broadcast_setting.custom_exceptions
                                .filter((exception: CustomException) => exception.type === "must-send")
                                .map((exception: CustomException) => {
                                    if (exception.exception_account_id) {
                                        return { ...exception.exception_account, state: "green-tick" };
                                    } else if (exception.exception_room_id) {
                                        return { ...exception.exception_room, state: "green-tick" };
                                    }
                                    return null;
                                }).filter(Boolean),
                            dont_send: data.data.broadcast_setting.custom_exceptions
                                .filter((exception: CustomException) => exception.type === "dont-send")
                                .map((exception: CustomException) => {
                                    if (exception.exception_account_id) {
                                        return { ...exception.exception_account, state: "red-cross" };
                                    } else if (exception.exception_room_id) {
                                        return { ...exception.exception_room, state: "red-cross" };
                                    }
                                    return null;
                                }).filter(Boolean),
                            send_to_company_categories: data.data.broadcast_setting.send_to_company_categories,
                            send_to_company_types: data.data.broadcast_setting.send_to_company_types,
                            send_to_account_designations: data.data.broadcast_setting.send_to_account_designations,
                        }
                    };
                    return transformedData;
                }
            },
        },
        resolver: zodResolver(actionType === "create" ? broadcastCreateFormSchema : broadcastEditFormSchema),
        defaultValues: {
            sender_phone: "",
            allow_personal: true,
            allow_group: false,
            applicable: [],
            unapplicable: [],
            must_send: [],
            dont_send: [],
            new_members: [],
            broadcast_name: "",
            send_to_account_designations: [],
            send_to_company_categories: [],
            send_to_company_types: [],
            ...(actionType === "edit" ? { id: "", room_id: "" } : {})
        }
    });

    const [filterType, setFilterType] = useState<IBroadcastFilterType>('all');

    // Use useMemo to make mustSendMembers and dontSendMembers reactive
    const { mustSendMembers, dontSendMembers } = useMemo(() => {
        return {
            mustSendMembers: formValues.getValues("must_send") || [],
            dontSendMembers: formValues.getValues("dont_send") || []
        };
    }, [formValues.watch("must_send"), formValues.watch("dont_send")]);

    const { data: teamMembers } = useList<IContact>({
        resource: "accounts/team-members",
    });

    // Extract team member IDs for categorization
    const teamMemberIds = useMemo(() => {
        return teamMembers?.data?.map(member => member.id) || [];
    }, [teamMembers?.data]);

    const { data: accountDesignations } = useCustom<string[]>({
        url: "/accounts/account-designations",
        method: "get"
    });

    const { data: companyCategories } = useCustom<string[]>({
        url: "/companies/company-categories",
        method: "get"
    });

    const { data: companyTypes } = useCustom<string[]>({
        url: "/companies/company-types",
        method: "get"
    });

    const senderPhone = formValues.watch("sender_phone");
    const senderPhoneNumber = senderPhone ? senderPhone.split(":")[1] ? senderPhone.split(":")[1]?.trim() : senderPhone : null;

    const senderAccountId = useMemo(() => {
        return teamMembers?.data?.find(acc => acc.user?.phone_number === Number(senderPhoneNumber))?.id;
    }, [teamMembers?.data, senderPhoneNumber]);

    const { data: groupRooms } = useCustom<IRoom[]>({
        url: `/rooms/group/${senderAccountId}`,
        method: "get",
        queryOptions: {
            enabled: !!senderAccountId && formValues.getValues("allow_group"),
            queryKey: [
                "group-rooms",
                senderAccountId,
                formValues.watch("allow_group")
            ]
        }
    });

    const { data: communityRooms } = useCustom<IRoom[]>({
        url: `/rooms/community/${senderAccountId}`,
        method: "get",
        queryOptions: {
            enabled: !!senderAccountId && formValues.getValues("allow_group"),
            queryKey: [
                "community-rooms",
                senderAccountId,
                formValues.watch("allow_group")
            ]
        }
    });

    const allRooms = useMemo(() => {
        return [...(groupRooms?.data ?? []), ...(communityRooms?.data ?? [])];
    }, [groupRooms?.data, communityRooms?.data]);

    const { data: allAccounts } = useCustom<IContact[]>({
        url: "/accounts/only-approved-users",
        method: "get",
        queryOptions: {
            enabled: !!senderAccountId && formValues.getValues("allow_personal"),
            queryKey: [
                "all-accounts",
                senderAccountId,
                formValues.watch("allow_personal")
            ]
        }
    });

    const { data: applicableAccountIds } = useCustom<string[]>({
        url: `/rooms/broadcasts/applicable-account-ids`,
        method: "get",
        config: {
            query: {
                company_categories: JSON.stringify(formValues.getValues("send_to_company_categories")),
                company_types: JSON.stringify(formValues.getValues("send_to_company_types")),
                account_designations: JSON.stringify(formValues.getValues("send_to_account_designations"))
            }
        },
        queryOptions: {
            enabled: !!senderAccountId && formValues.watch("allow_personal") && (formValues.watch("send_to_company_categories")?.length > 0 || formValues.watch("send_to_company_types")?.length > 0 || formValues.watch("send_to_account_designations")?.length > 0),
            queryKey: [
                "applicable-account-ids",
                senderAccountId,
                formValues.watch("allow_personal"),
                formValues.watch("send_to_company_categories"),
                formValues.watch("send_to_company_types"),
                formValues.watch("send_to_account_designations")
            ]
        }
    });

    const applicableAccounts = allAccounts?.data?.filter((account) => applicableAccountIds?.data?.includes(account.id));

    const { data: applicableRooms } = useCustom<IRoom[]>({
        url: `/rooms/broadcasts/applicable-rooms/${senderAccountId}`,
        method: "get",
        config: {
            query: {
                company_categories: JSON.stringify(formValues.watch("send_to_company_categories")),
                company_types: JSON.stringify(formValues.watch("send_to_company_types")),
                account_designations: JSON.stringify(formValues.watch("send_to_account_designations"))
            }
        },
        queryOptions: {
            enabled: !!senderAccountId && formValues.watch("allow_group") && (formValues.watch("send_to_company_categories")?.length > 0 || formValues.watch("send_to_company_types")?.length > 0 || formValues.watch("send_to_account_designations")?.length > 0),
            queryKey: [
                "applicable-rooms",
                senderAccountId,
                formValues.watch("allow_group"),
                formValues.watch("send_to_company_categories"),
                formValues.watch("send_to_company_types"),
                formValues.watch("send_to_account_designations")
            ]
        }
    });

    const { handleSubmit, refineCore: { onFinish } } = formValues;
    const t = useTranslate();

    if (actionType === "edit") {
        formValues.register("id");
        formValues.register("room_id");
    }

    const allMembers: IBroadcastMemberSchema[] = useMemo(() => {
        // Get all room IDs that are applicable
        const applicableRoomIds = applicableRooms?.data?.map(room => room.id) || [];

        // Check if any filters are applied
        const hasFilters = (formValues.watch("send_to_account_designations")?.length > 0 ||
            formValues.watch("send_to_company_categories")?.length > 0 ||
            formValues.watch("send_to_company_types")?.length > 0);

        // Process all accounts
        const accountMembers = [...(allAccounts?.data ?? []), ...(teamMembers?.data ?? [])]?.map(account => ({
            ...account,
            state: hasFilters && applicableAccountIds?.data?.includes(account.id)
                ? mustSendMembers?.some((m: IBroadcastMemberSchema) => m.id === account.id)
                    ? 'green-tick'
                    : dontSendMembers?.some((m: IBroadcastMemberSchema) => m.id === account.id)
                        ? 'red-cross'
                        : 'blue-tick'
                : mustSendMembers?.some((m: IBroadcastMemberSchema) => m.id === account.id)
                    ? 'green-tick'
                    : dontSendMembers?.some((m: IBroadcastMemberSchema) => m.id === account.id)
                        ? 'red-cross'
                        : 'unchecked'
        })) || [];

        // Process all rooms
        const roomMembers = allRooms?.map(room => ({
            ...room,
            state: hasFilters && applicableRoomIds.includes(room.id)
                ? mustSendMembers?.some((m: IBroadcastMemberSchema) => m.id === room.id)
                    ? 'green-tick'
                    : dontSendMembers?.some((m: IBroadcastMemberSchema) => m.id === room.id)
                        ? 'red-cross'
                        : 'blue-tick'
                : mustSendMembers?.some((m: IBroadcastMemberSchema) => m.id === room.id)
                    ? 'green-tick'
                    : dontSendMembers?.some((m: IBroadcastMemberSchema) => m.id === room.id)
                        ? 'red-cross'
                        : 'unchecked'
        })) || [];

        return [...accountMembers, ...roomMembers] as IBroadcastMemberSchema[];
    }, [applicableAccounts, applicableRooms, allAccounts, allRooms, formValues.watch("send_to_account_designations"), formValues.watch("send_to_company_categories"), formValues.watch("send_to_company_types")]);

    const filteredMembers = useMemo(() => {
        // Get the current must_send and dont_send members
        const mustSendIds = formValues.getValues("must_send")?.map((m: IBroadcastMemberSchema) => m.id) || [];
        const dontSendIds = formValues.getValues("dont_send")?.map((m: IBroadcastMemberSchema) => m.id) || [];

        // Get the current group and personal settings
        const allowGroup = formValues.getValues("allow_group");
        const allowPersonal = formValues.getValues("allow_personal");

        // First filter based on group/personal settings
        let filteredByType = allMembers.filter(member => {
            // If it's a room (group) and group is not allowed, filter it out
            if (!('user' in member) && !allowGroup) {
                return false;
            }

            // If it's an account (personal) and personal is not allowed, filter it out
            if ('user' in member && !allowPersonal) {
                return false;
            }

            return true;
        });

        // Then apply the regular filters
        if (filterType === 'applicable') {
            // Filter for applicable members that are not already in must_send or dont_send
            return filteredByType.filter(member =>
                member.state === 'blue-tick' &&
                !mustSendIds.includes(member.id) &&
                !dontSendIds.includes(member.id)
            );
        }
        if (filterType === 'unapplicable') return filteredByType.filter(member => member.state === 'unchecked');

        return filteredByType.map(member => {
            const mustSendMember = formValues.getValues("must_send")?.find((m: IBroadcastMemberSchema) => m.id === member.id);
            if (mustSendMember) return { ...mustSendMember, state: 'green-tick' };

            const dontSendMember = formValues.getValues("dont_send")?.find((m: IBroadcastMemberSchema) => m.id === member.id);
            if (dontSendMember) return { ...dontSendMember, state: 'red-cross' };

            return member;
        });
    }, [
        filterType,
        allMembers,
        mustSendMembers,
        dontSendMembers,
        // Add dependencies on group/personal checkboxes
        formValues.watch("allow_group"),
        formValues.watch("allow_personal"),
        // Add dependency on form values to ensure the filter updates when must_send or dont_send changes
        formValues.watch("must_send"),
        formValues.watch("dont_send"),
    ]);

    // Add count calculations for all filter types
    const memberCounts = useMemo(() => {
        // Get the current must_send and dont_send members
        const mustSendIds = formValues.getValues("must_send")?.map((m: IBroadcastMemberSchema) => m.id) || [];
        const dontSendIds = formValues.getValues("dont_send")?.map((m: IBroadcastMemberSchema) => m.id) || [];

        // Get the current group and personal settings
        const allowGroup = formValues.getValues("allow_group");
        const allowPersonal = formValues.getValues("allow_personal");

        // First filter based on group/personal settings
        let filteredByType = allMembers.filter(member => {
            // If it's a room (group) and group is not allowed, filter it out
            if (!('user' in member) && !allowGroup) {
                return false;
            }

            // If it's an account (personal) and personal is not allowed, filter it out
            if ('user' in member && !allowPersonal) {
                return false;
            }

            return true;
        });

        // Calculate counts for each filter type
        const allCount = filteredByType.length;
        const applicableCount = filteredByType.filter(member =>
            member.state === 'blue-tick' &&
            !mustSendIds.includes(member.id) &&
            !dontSendIds.includes(member.id)
        ).length;
        const unapplicableCount = filteredByType.filter(member => member.state === 'unchecked').length;

        return {
            all: allCount,
            applicable: applicableCount,
            unapplicable: unapplicableCount
        };
    }, [
        allMembers,
        formValues.watch("allow_group"),
        formValues.watch("allow_personal"),
        formValues.watch("must_send"),
        formValues.watch("dont_send"),
        formValues.watch("send_to_account_designations"),
        formValues.watch("send_to_company_categories"),
        formValues.watch("send_to_company_types")
    ]);

    // Update filtered title to include count
    const filteredTitleWithCount = useMemo(() => {
        const baseTitle = filterType === 'applicable' ? "Applicable" : 
                         filterType === 'unapplicable' ? "Unapplicable" : "All";
        const count = memberCounts[filterType];
        return `${baseTitle} (${count})`;
    }, [filterType, memberCounts]);

    const getDontSendInfo = useMemo(() => {
        return {
            text: "Groups containing these members will not receive any messages from this broadcast",
        };
    }, [dontSendMembers]);

    const handleMultiStateChange = (member: IBroadcastMemberSchema, currentState: CheckboxState, nextState: CheckboxState) => {
        const updatedMember = { ...member, state: nextState };

        // First, remove the member from its current list if it's in one
        if (currentState === 'green-tick') {
            formValues.setValue('must_send', mustSendMembers.filter((m: IBroadcastMemberSchema) => m.id !== member.id));
        } else if (currentState === 'red-cross') {
            formValues.setValue('dont_send', dontSendMembers.filter((m: IBroadcastMemberSchema) => m.id !== member.id));
        }

        // Then, add the member to its new list if needed
        if (nextState === 'green-tick') {
            formValues.setValue('must_send', [...mustSendMembers.filter((m: IBroadcastMemberSchema) => m.id !== member.id), updatedMember]);
        } else if (nextState === 'red-cross') {
            formValues.setValue('dont_send', [...dontSendMembers.filter((m: IBroadcastMemberSchema) => m.id !== member.id), updatedMember]);
        }
    };

    const { data: userData } = useGetIdentity<IContact>();

    const onSubmit = handleSubmit((values) => {
        const valuesToSubmit = {
            ...(actionType === "edit" ? { id: values.id } : {}),
            broadcast_setting: {
                ...(actionType === "edit" ? { room_id: values.room_id } : {}),
                send_to_contact_tag: values.send_to_contact_tag,
                custom_exceptions: [
                    ...(values.must_send || []).map((ms: IBroadcastMemberSchema) => ({
                        type: "must-send",
                        exception_account_id: 'user' in ms ? ms.id : null,
                        exception_room_id: 'user' in ms ? null : ms.id
                    })),
                    ...(values.dont_send || []).map((ds: IBroadcastMemberSchema) => ({
                        type: "dont-send",
                        exception_account_id: 'user' in ds ? ds.id : null,
                        exception_room_id: 'user' in ds ? null : ds.id
                    }))
                ],
                send_to_company_categories: values.send_to_company_categories,
                send_to_company_types: values.send_to_company_types,
                send_to_account_designations: values.send_to_account_designations,
                sender_account_id: senderAccountId,
                send_to: values.allow_group ? values.allow_personal ? ["group", "personal"] : ["group"] : ["personal"],
            },
            created_by_account_id: userData?.id,
            description: values.description,
            name: values.broadcast_name,
            type: "broadcast"
        };
        onFinish(valuesToSubmit);
    });

    return (
        <CreateOrEdit actionType={actionType} formValues={formValues} handleSubmit={onSubmit} title={{ edit: `Edit Broadcast #${formValues.getValues("broadcast_name")}` }}>
            <Stack direction="column" sx={{ p: 2 }}>
                <Stack direction="column" spacing={2}>
                    <Stack direction="row" spacing={2}>
                        <TextFieldElement
                            name="broadcast_name"
                            label={t("broadcast.fields.broadcastName")}
                            sx={{ flex: 1 }}
                            required
                            autoComplete="no-name"
                            slotProps={{
                                inputLabel: {
                                    sx: {
                                        '& .MuiFormLabel-asterisk': {
                                            color: 'error.main',
                                            fontSize: '24px',
                                        },
                                    },
                                },
                            }}
                            size="small"
                        />
                        <RHFAutoCompleteString
                            name="sender_phone"
                            label={t("broadcast.fields.senderPhone")}
                            options={(teamMembers?.data ?? [])?.map((member) => `${(member.name && member.user?.name) ? `${member.name} (${member.user?.name})` : member.name ? member.name : member.user?.name ? `(${member.user?.name})` : ""}: ${member.user?.phone_number}`) ?? []}
                            multiple={false}
                            sx={{ flex: 1 }}
                            createNew={false}
                            required
                            size="small"
                        />
                        <Stack direction="row" justifyContent="center" alignItems="center" gap={2} sx={{ flex: 1 }}>
                            <Typography variant="body1">Send to:</Typography>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <CheckboxElement name="allow_group" color="primary" label={t("broadcast.fields.group")} />
                                <CheckboxElement name="allow_personal" color="primary" label={t("broadcast.fields.one-to-one")} />
                            </Stack>
                        </Stack>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center">
                        <RHFAutoCompleteString
                            name="send_to_account_designations"
                            label={t("broadcast.fields.sendToAccountDesignation")}
                            options={accountDesignations?.data ?? []}
                            createNew={false}
                            multiple={true}
                            sx={{ flex: 1 }}
                            selectAll={true}
                            size="small"
                        />
                        <RHFAutoCompleteString
                            name="send_to_company_categories"
                            label={t("broadcast.fields.sendToCompanyCategory")}
                            options={companyCategories?.data ?? []}
                            sx={{ flex: 1 }}
                            createNew={false}
                            multiple={true}
                            selectAll={true}
                            size="small"
                        />
                        <RHFAutoCompleteString
                            name="send_to_company_types"
                            label={t("broadcast.fields.sendToCompanyType")}
                            options={companyTypes?.data ?? []}
                            sx={{ flex: 1 }}
                            createNew={false}
                            multiple={true}
                            selectAll={true}
                            size="small"
                        />
                    </Stack>
                </Stack>

                <Grid container spacing={3} sx={{ mt: 2, mb: 2 }}>
                    <Grid size={{ xs: 4 }}>
                        <BroadcastMemberColumn
                            title={filteredTitleWithCount}
                            members={filteredMembers}
                            onMultiStateChange={handleMultiStateChange}
                            filterType={filterType}
                            onFilterChange={(type) => setFilterType(type)}
                            allowSearch={true}
                            memberCounts={memberCounts}
                            teamMemberIds={teamMemberIds}
                        />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <BroadcastMemberColumn
                            title={`Must send (${formValues.watch("must_send")?.length || 0})`}
                            members={formValues.watch("must_send")}
                            onMultiStateChange={handleMultiStateChange}
                            titleIcon={<CheckBoxOutlinedIcon sx={{ color: 'success.main', fontSize: 32 }} />}
                            teamMemberIds={teamMemberIds}
                        />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <BroadcastMemberColumn
                            title={`Dont send (${formValues.watch("dont_send")?.length || 0})`}
                            members={formValues.watch("dont_send")}
                            onMultiStateChange={handleMultiStateChange}
                            titleIcon={<BlockOutlinedIcon sx={{ color: 'error.main', fontSize: 32 }} />}
                            onInfoHover={getDontSendInfo}
                            teamMemberIds={teamMemberIds}
                        />
                    </Grid>
                </Grid>
            </Stack>
        </CreateOrEdit>
    );
};